import React, { useState, useEffect, useCallback } from 'react';
import client from './api/client';
import PageHeader from './components/PageHeader';
import './App.css';

function SettingsPage() {

  // Authentication state
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Characters state
  const [characters, setCharacters] = useState([]);
  const [addingCharacter, setAddingCharacter] = useState(false);
  const [error, setError] = useState('');

  // Scope tracking state
  const [scopeStatus, setScopeStatus] = useState({});
  const [charactersNeedingReauth, setCharactersNeedingReauth] = useState(0);

  const fetchUserInfo = useCallback(async () => {
    try {
      const response = await client.get('/auth/user/');
      if (response.data.authenticated) {
        setUser(response.data.character);
        return true;
      } else {
        setUser(null);
        return false;
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
      setUser(null);
      return false;
    }
  }, []);

  const fetchCharacters = useCallback(async () => {
    try {
      const response = await client.get('/auth/characters/');
      if (response.data.success) {
        setCharacters(response.data.characters);
      }
    } catch (error) {
      console.error('Error fetching characters:', error);
      setError('Failed to load characters');
    }
  }, []);

  const fetchScopeStatus = useCallback(async () => {
    try {
      const response = await client.get('/auth/characters/scope-status/');
      if (response.data.success) {
        // Build a map of character_id -> scope status
        const statusMap = {};
        let needsReauth = 0;
        response.data.characters.forEach(char => {
          statusMap[char.character_id] = {
            granted_scopes: char.granted_scopes,
            missing_scopes: char.missing_scopes,
            requires_reauth: char.requires_reauth
          };
          if (char.requires_reauth) {
            needsReauth++;
          }
        });
        setScopeStatus(statusMap);
        setCharactersNeedingReauth(needsReauth);
      }
    } catch (error) {
      console.error('Error fetching scope status:', error);
    }
  }, []);

  const handleAddCharacter = async () => {
    setAddingCharacter(true);
    setError('');
    try {
      // Get current page path to redirect back to after auth
      const currentPath = window.location.pathname;
      const response = await client.get(`/auth/eve/add-character/?redirect=${encodeURIComponent(currentPath)}`);
      window.location.href = response.data.login_url;
    } catch (error) {
      console.error('Error starting character addition:', error);
      setError('Failed to start character addition process');
      setAddingCharacter(false);
    }
  };

  const handleRemoveCharacter = async (characterId) => {
    if (!window.confirm('Are you sure you want to remove this character? This will revoke its API access.')) {
      return;
    }

    try {
      const response = await client.delete(`/auth/characters/${characterId}/remove/`);
      if (response.data.success) {
        // Refresh characters list
        await fetchCharacters();
      } else {
        setError(response.data.error || 'Failed to remove character');
      }
    } catch (error) {
      console.error('Error removing character:', error);
      setError('Failed to remove character');
    }
  };

  const handleSetPrimary = async (characterId) => {
    try {
      const response = await client.post(`/auth/characters/${characterId}/set-primary/`);
      if (response.data.success) {
        // Refresh characters list and user info
        await Promise.all([fetchCharacters(), fetchUserInfo()]);
      } else {
        setError(response.data.error || 'Failed to set primary character');
      }
    } catch (error) {
      console.error('Error setting primary character:', error);
      setError('Failed to set primary character');
    }
  };

  const handleCorpCeoToggle = async (characterId, isCorpCeo) => {
    try {
      const response = await client.post(`/auth/characters/${characterId}/corp-ceo/`, {
        is_corp_ceo: isCorpCeo
      });
      if (response.data.success) {
        // Refresh characters list
        await fetchCharacters();
      } else {
        setError(response.data.error || 'Failed to update corp CEO status');
      }
    } catch (error) {
      console.error('Error updating corp CEO status:', error);
      setError('Failed to update corp CEO status');
    }
  };

  // Load user info and characters on mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const authenticated = await fetchUserInfo();
      if (authenticated) {
        await Promise.all([fetchCharacters(), fetchScopeStatus()]);
      }
      setLoading(false);
    };

    loadData();
  }, [fetchUserInfo, fetchCharacters, fetchScopeStatus]);

  // Handle auth callback results for character addition and re-authentication
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('char_add_success') === 'true') {
      // Clear URL parameters and refresh characters
      window.history.replaceState({}, '', window.location.pathname);
      fetchCharacters();
      fetchScopeStatus();
      setAddingCharacter(false);
    } else if (params.get('char_update_success') === 'true') {
      // Character was re-authenticated with new scopes
      window.history.replaceState({}, '', window.location.pathname);
      fetchCharacters();
      fetchScopeStatus();
      setAddingCharacter(false);
      setError(''); // Clear any previous errors
    } else if (params.get('char_add_error')) {
      // Handle character addition error
      setError(`Character addition failed: ${params.get('char_add_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
      setAddingCharacter(false);
    }
  }, [fetchCharacters, fetchScopeStatus]);

  if (loading) {
    return (
      <div className="page">
        <PageHeader title="Account Settings" />
        <div className="page-status">Loading settings...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="page">
        <PageHeader title="Account Settings" />
        <div className="page-status">Log in with your EVE Online character to manage your settings.</div>
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader title="Account Settings">
        <button onClick={handleAddCharacter} disabled={addingCharacter} className="btn btn-primary">
          {addingCharacter ? 'Adding...' : 'Add character'}
        </button>
      </PageHeader>
      <div className="page-content">
        {error && <div className="error-message">{error}</div>}

        {charactersNeedingReauth > 0 && (
          <div className="notice notice-warn">
            <strong>
              {charactersNeedingReauth} character{charactersNeedingReauth > 1 ? 's need' : ' needs'} new permissions.
            </strong>{' '}
            Use "Add character" and log in again with each character marked "Missing" below.
          </div>
        )}

        <section className="panel">
          <div className="panel-head">
            <h2>Characters</h2>
            <span className="panel-sub">Market orders and wallet data from every character are combined; each keeps its own ESI token.</span>
          </div>
          {characters.length === 0 ? (
            <div className="table-empty">No additional characters yet. Your main character is included automatically.</div>
          ) : (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Character</th>
                    <th>Corporation</th>
                    <th>Alliance</th>
                    <th>Role</th>
                    <th>Scopes</th>
                    <th title="Corp CEOs also fetch corporation orders">Corp CEO</th>
                    <th>Added</th>
                    <th aria-label="Actions"></th>
                  </tr>
                </thead>
                <tbody>
                  {characters.map((character) => {
                    const scopes = scopeStatus[character.character_id];
                    return (
                      <tr key={character.character_id}>
                        <td>
                          <span className="lp-char">
                            <img
                              src={character.portrait_64x64}
                              alt=""
                              className="char-chip-portrait"
                              onError={(e) => { e.target.style.visibility = 'hidden'; }}
                            />
                            <b>{character.character_name}</b>
                          </span>
                        </td>
                        <td>{character.corporation_name || 'Unknown'}</td>
                        <td className={character.alliance_name ? '' : 'text-faint'}>{character.alliance_name || 'None'}</td>
                        <td>
                          <span className={`pill ${character.is_primary ? 'pill-accent' : 'pill-neutral'}`}>
                            {character.is_primary ? 'Primary' : 'Secondary'}
                          </span>
                        </td>
                        <td>
                          {!scopes ? (
                            <span className="text-faint">Loading...</span>
                          ) : scopes.requires_reauth ? (
                            <span className="pill pill-warn" title={`Missing scopes: ${scopes.missing_scopes.join(', ')}`}>
                              Missing {scopes.missing_scopes.length}
                            </span>
                          ) : scopes.granted_scopes.length === 0 ? (
                            <span className="pill pill-neg" title="No scopes stored; log in again with this character">No scopes</span>
                          ) : (
                            <span className="pill pill-pos" title={`All required scopes granted (${scopes.granted_scopes.length} total)`}>All granted</span>
                          )}
                        </td>
                        <td>
                          <input
                            type="checkbox"
                            checked={character.is_corp_ceo || false}
                            onChange={(e) => handleCorpCeoToggle(character.character_id, e.target.checked)}
                            aria-label={`${character.character_name} is corp CEO`}
                          />
                        </td>
                        <td>{new Date(character.created_at).toLocaleDateString()}</td>
                        <td className="num">
                          {!character.is_primary && (
                            <span className="btn-row row-action settings-actions">
                              <button onClick={() => handleSetPrimary(character.character_id)} className="btn btn-sm">
                                Make primary
                              </button>
                              <button onClick={() => handleRemoveCharacter(character.character_id)} className="btn btn-danger btn-sm">
                                Remove
                              </button>
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default SettingsPage;